import { useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Sparkles, ExternalLink } from "lucide-react";

/**
 * Staff-only form to enter / update DecisionPoint metrics for a screening.
 * Saves to PATCH /screenings/{id}/decisionpoint.
 *
 * 2026-02 · DecisionPoint ⇄ Operations consolidation. The six verification
 * fields (employment_verified/summary, landlord_verified/summary,
 * document_validation/summary) are NO LONGER editable here — they are
 * projected server-side from `verifications.<track>.*`. Staff jump to
 * Operations to change them. Everything else on this form is still owned
 * by DecisionPoint.
 */
export default function DecisionPointEntryForm({ screeningId, dp, onSaved }) {
  const initial = dp || {};
  const [form, setForm] = useState({
    dp_score: initial.dp_score ?? "",
    dp_score_status: initial.dp_score_status ?? (initial.dp_score != null ? "scored" : ""),
    fico_score: initial.fico_score != null && initial.fico_score !== -9999 ? initial.fico_score : "",
    fico_status: initial.fico_status
      ?? (initial.fico_score === -9999 ? "insufficient_history" : initial.fico_score != null ? "scored" : ""),
    eviction_records_found: boolToString(initial.eviction_records_found),
    eviction_count: initial.eviction_count ?? "",
    eviction_summary: initial.eviction_summary ?? "",
    criminal_records_found: boolToString(initial.criminal_records_found),
    criminal_summary: initial.criminal_summary ?? "",
    monthly_income: initial.monthly_income ?? "",
    staff_notes: initial.staff_notes ?? "",
    staff_verdict_override: initial.staff_verdict_override ?? "",
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {};
      if (form.dp_score_status === "not_available") {
        payload.dp_score_status = "not_available";
      } else if (form.dp_score !== "") {
        payload.dp_score = parseInt(form.dp_score, 10);
        payload.dp_score_status = "scored";
      }
      if (form.fico_status === "not_available" || form.fico_status === "insufficient_history") {
        payload.fico_status = form.fico_status;
      } else if (form.fico_score !== "") {
        payload.fico_score = parseInt(form.fico_score, 10);
        payload.fico_status = "scored";
      }

      if (form.eviction_records_found !== "") payload.eviction_records_found = form.eviction_records_found === "true";
      if (form.eviction_count !== "") payload.eviction_count = parseInt(form.eviction_count, 10);
      if (form.eviction_summary) payload.eviction_summary = form.eviction_summary;

      if (form.criminal_records_found !== "") payload.criminal_records_found = form.criminal_records_found === "true";
      if (form.criminal_summary) payload.criminal_summary = form.criminal_summary;

      if (form.monthly_income !== "") payload.monthly_income = parseFloat(form.monthly_income);

      if (form.staff_notes) payload.staff_notes = form.staff_notes;
      if (form.staff_verdict_override) payload.staff_verdict_override = form.staff_verdict_override;

      const { data } = await apiClient.patch(`/screenings/${screeningId}/decisionpoint`, payload);
      toast.success("DecisionPoint saved");
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const suggested = dp?.suggested_verdict;

  return (
    <div className="bg-white border border-[#0A192F]/10 rounded-sm p-6" data-testid="dp-entry-form">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="font-overline text-[#4A5568]">NTN Staff · DecisionPoint entry</div>
          <h3 className="font-serif text-2xl text-[#0A192F] mt-1">Enter screening results</h3>
          <p className="text-xs text-[#4A5568] mt-1">Source values from TransUnion / DecisionPoint Plus. Employment, Landlord and Documents are now managed exclusively in Operations.</p>
        </div>
        {suggested && (
          <div className="inline-flex items-center gap-2 bg-[#F3EFEA] px-3 py-1.5 rounded-sm text-xs text-[#0A192F]" data-testid="dp-auto-suggested">
            <Sparkles className="h-3.5 w-3.5" />
            <span className="font-mono tracking-[0.12em]">AUTO-SUGGESTED · {suggested.toUpperCase()}</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="DecisionPoint Score">
          <div className="space-y-2">
            <Select value={form.dp_score_status || "scored"} onValueChange={(v) => set("dp_score_status", v)}>
              <SelectTrigger className="rounded-sm h-11" data-testid="dp-score-status"><SelectValue placeholder="Scored" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="scored">Scored</SelectItem>
                <SelectItem value="not_available">N/A — no score</SelectItem>
              </SelectContent>
            </Select>
            {(form.dp_score_status || "scored") === "scored" && (
              <Input type="number" min="0" max="100" value={form.dp_score} onChange={(e) => set("dp_score", e.target.value)}
                     className="rounded-sm h-11" placeholder="0-100 · e.g. 78" data-testid="dp-input-score" />
            )}
          </div>
        </Field>
        <Field label="FICO Score">
          <div className="space-y-2">
            <Select value={form.fico_status || "scored"} onValueChange={(v) => set("fico_status", v)}>
              <SelectTrigger className="rounded-sm h-11" data-testid="dp-fico-status"><SelectValue placeholder="Scored" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="scored">Scored</SelectItem>
                <SelectItem value="insufficient_history">Insufficient Credit History (-9999)</SelectItem>
                <SelectItem value="not_available">N/A — no score</SelectItem>
              </SelectContent>
            </Select>
            {(form.fico_status || "scored") === "scored" && (
              <Input type="number" min="300" max="850" value={form.fico_score} onChange={(e) => set("fico_score", e.target.value)}
                     className="rounded-sm h-11" placeholder="300-850 · e.g. 710" data-testid="dp-input-fico" />
            )}
          </div>
        </Field>

        <Field label="Eviction records found">
          <Select value={form.eviction_records_found} onValueChange={(v) => set("eviction_records_found", v)}>
            <SelectTrigger className="rounded-sm h-11" data-testid="dp-input-eviction"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="false">No records</SelectItem>
              <SelectItem value="true">Records found</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Eviction count (if any)">
          <Input type="number" min="0" value={form.eviction_count} onChange={(e) => set("eviction_count", e.target.value)}
                 className="rounded-sm h-11" placeholder="0" data-testid="dp-input-eviction-count" />
        </Field>
        <Field label="Eviction summary" full>
          <Textarea rows={2} value={form.eviction_summary} onChange={(e) => set("eviction_summary", e.target.value)}
                    placeholder="Optional — case ref, jurisdiction, disposition" className="rounded-sm" />
        </Field>

        <Field label="Criminal records found">
          <Select value={form.criminal_records_found} onValueChange={(v) => set("criminal_records_found", v)}>
            <SelectTrigger className="rounded-sm h-11" data-testid="dp-input-criminal"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="false">Clear</SelectItem>
              <SelectItem value="true">Records found</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Criminal summary">
          <Input value={form.criminal_summary} onChange={(e) => set("criminal_summary", e.target.value)}
                 className="rounded-sm h-11" placeholder="Optional — offense, year, jurisdiction" />
        </Field>

        <Field label="Monthly income ($)">
          <Input type="number" step="0.01" min="0" value={form.monthly_income} onChange={(e) => set("monthly_income", e.target.value)}
                 className="rounded-sm h-11" placeholder="e.g. 6500" data-testid="dp-input-income" />
        </Field>

        {/* Employment / Landlord / Documents — moved to Operations. Staff jump
             directly to the correct verification workspace. */}
        <div className="md:col-span-2 border-t border-slate-200 pt-5 mt-1" data-testid="dp-ops-links">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568] mb-2">
            Managed in Operations
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Link to={`/staff-ops/employment/${screeningId}`}
                  className="flex items-center justify-between rounded-sm border border-slate-200 bg-slate-50/50 hover:bg-white hover:border-slate-400 px-3 py-2.5 transition-colors"
                  data-testid="dp-ops-link-employment">
              <span className="text-sm text-slate-800">Employment Verification</span>
              <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
            </Link>
            <Link to={`/staff-ops/landlord/${screeningId}`}
                  className="flex items-center justify-between rounded-sm border border-slate-200 bg-slate-50/50 hover:bg-white hover:border-slate-400 px-3 py-2.5 transition-colors"
                  data-testid="dp-ops-link-landlord">
              <span className="text-sm text-slate-800">Landlord Verification</span>
              <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
            </Link>
            <Link to={`/staff-ops/documents/${screeningId}`}
                  className="flex items-center justify-between rounded-sm border border-slate-200 bg-slate-50/50 hover:bg-white hover:border-slate-400 px-3 py-2.5 transition-colors"
                  data-testid="dp-ops-link-documents">
              <span className="text-sm text-slate-800">Document Validation</span>
              <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
            </Link>
          </div>
          <p className="text-[11px] text-slate-500 mt-2 italic">
            Verification status and summary are now sourced from Operations and rendered read-only in the DecisionPoint report.
          </p>
        </div>

        <Field label="Internal case notes · NTN only" full>
          <Textarea rows={3} value={form.staff_notes} onChange={(e) => set("staff_notes", e.target.value)}
                    placeholder="Narrative summary, mitigating factors, conditions, etc."
                    className="rounded-sm" data-testid="dp-input-notes" />
          <p className="text-[11px] text-slate-500 mt-1.5">
            Never shown to the subscriber. To share an update with the property manager, post a
            &ldquo;Shared with PM&rdquo; note from the screening workflow panel.
          </p>
        </Field>

        <Field label="Verdict override (optional)" full>
          <Select value={form.staff_verdict_override} onValueChange={(v) => set("staff_verdict_override", v)}>
            <SelectTrigger className="rounded-sm h-11" data-testid="dp-input-override"><SelectValue placeholder="Use auto-suggested" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="approve">Approve</SelectItem>
              <SelectItem value="conditional">Conditional</SelectItem>
              <SelectItem value="deny">Deny</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-[#4A5568] mt-1">Leave blank to use the auto-suggested verdict computed from these metrics.</p>
        </Field>
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <Button onClick={submit} disabled={busy} className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm h-11 px-5" data-testid="dp-save-btn">
          <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save DecisionPoint"}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function boolToString(v) {
  if (v === true) return "true";
  if (v === false) return "false";
  return "";
}
